/**
 * PinnedMessagesPanel — Pannello laterale con i messaggi fissati
 * Notion-style design
 */

import React, { useEffect, useState } from 'react';
import { X, Pin, ArrowRight, Loader2 } from 'lucide-react';
import { useTeamChatStore, TeamChatMessage } from '../../lib/stores/teamChatStore';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface PinnedMessagesPanelProps {
  chatId: string;
  onClose: () => void;
  onScrollToMessage?: (messageId: string) => void;
}

export function PinnedMessagesPanel({ chatId, onClose, onScrollToMessage }: PinnedMessagesPanelProps) {
  const { getPinnedMessages } = useTeamChatStore();
  const [pinnedMessages, setPinnedMessages] = useState<TeamChatMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPinnedMessages(chatId).then(msgs => {
      if (!cancelled) {
        setPinnedMessages(msgs);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [chatId, getPinnedMessages]);

  // Truncate content for preview
  const truncate = (text: string, max = 120) => {
    if (!text) return '';
    // Try to parse JSON content for special types
    try {
      const parsed = JSON.parse(text);
      if (parsed.entity_data?.name) return `📎 ${parsed.entity_data.name}`;
      if (parsed.comment_content) return `💬 ${parsed.comment_content}`;
      if (parsed.title) return parsed.title;
    } catch { /* plain text */ }
    return text.length > max ? text.slice(0, max) + '…' : text;
  };

  return (
    <div
      className="w-[280px] flex flex-col h-full flex-shrink-0"
      style={{ borderLeft: '1px solid rgba(55,53,47,0.09)', backgroundColor: '#FBFBFA' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2.5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}
      >
        <div className="flex items-center gap-1.5">
          <Pin className="w-3.5 h-3.5" style={{ color: '#2383E2' }} strokeWidth={1.5} />
          <span className="text-[13px] font-semibold" style={{ color: '#37352F' }}>
            Messaggi fissati
          </span>
          {pinnedMessages.length > 0 && (
            <span className="text-[11px] px-1.5 rounded-full" style={{ backgroundColor: 'rgba(35,131,226,0.1)', color: '#2383E2' }}>
              {pinnedMessages.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] transition-colors"
        >
          <X className="w-3.5 h-3.5" style={{ color: '#787774' }} strokeWidth={1.5} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2383E2' }} />
          </div>
        ) : pinnedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <Pin className="w-8 h-8 mb-2" style={{ color: '#D3D1CB' }} strokeWidth={1} />
            <p className="text-[13px]" style={{ color: '#9B9A97' }}>Nessun messaggio fissato</p>
            <p className="text-[11px] mt-1" style={{ color: '#D3D1CB' }}>
              Fissa i messaggi importanti per ritrovarli velocemente
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {pinnedMessages.map(msg => (
              <button
                key={msg.id}
                onClick={() => onScrollToMessage?.(msg.id)}
                className="w-full text-left rounded-lg px-3 py-2.5 transition-colors group"
                style={{ backgroundColor: 'white', border: '1px solid rgba(55,53,47,0.06)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)'; e.currentTarget.style.borderColor = 'rgba(55,53,47,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.borderColor = 'rgba(55,53,47,0.06)'; }}
              >
                {/* Author + time */}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-semibold" style={{ color: '#37352F' }}>
                    {msg.sender?.full_name || 'Sistema'}
                  </span>
                  <span className="text-[10px]" style={{ color: '#9B9A97' }}>
                    {formatDistanceToNow(new Date(msg.created_at), { addSuffix: false, locale: it })}
                  </span>
                </div>
                {/* Content preview */}
                <p className="text-[12px] leading-relaxed line-clamp-3" style={{ color: '#787774' }}>
                  {truncate(msg.content)}
                </p>
                {/* Go to message hint */}
                <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[10px]" style={{ color: '#2383E2' }}>Vai al messaggio</span>
                  <ArrowRight className="w-2.5 h-2.5" style={{ color: '#2383E2' }} strokeWidth={2} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
